import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, LinearProgress } from '@mui/material';
import { useAuthStore } from './stores/authStore';
import HomePage from './pages/HomePage.tsx';
import LoginPage from './pages/auth/LoginPage';
import ActivationPage from './pages/auth/ActivationPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ProtectedRoute from './components/ProtectedRoute';
import UserMenu from './components/UserMenu';
import { TOOLS, getToolByPathname } from './tools/registry';

const AccountPage = lazy(() => import('./pages/AccountPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const CostsPage = lazy(() => import('./pages/facturation/pages/CostsPage'));
const CostDetailPage = lazy(() => import('./pages/facturation/pages/CostDetailPage'));

const PUBLIC_PATHS = ['/login', '/activation', '/mot-de-passe-oublie'];

/** Pages hors outils affichées avec un titre et une flèche retour. */
const PAGES: { basePath: string; name: string }[] = [
  { basePath: '/compte', name: 'Mon compte' },
  { basePath: '/admin', name: 'Administration' },
  { basePath: '/facturation', name: 'Facturation' },
];

const getPageByPathname = (pathname: string) =>
  PAGES.find((p) => pathname === p.basePath || pathname.startsWith(`${p.basePath}/`));

function TopBar() {
  const location = useLocation();
  const activeSection = getToolByPathname(location.pathname) ?? getPageByPathname(location.pathname);
  const title = activeSection?.name;
  useEffect(() => {
    document.title = title ? `${title} · JustiSch` : 'JustiSch';
  }, [title]);
  // Sous-page (détail) : le nom de la section devient un lien vers sa base
  const isSubPage = Boolean(activeSection && location.pathname !== activeSection.basePath);

  return (
    <AppBar position="sticky" color="inherit" elevation={0} sx={{ bgcolor: 'background.paper' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
        <Box component="nav" aria-label="fil d'Ariane" sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Box
            component={Link}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit', flexShrink: 0 }}
          >
            <Box component="img" src="/logo-vert.svg" alt="JustiSch" sx={{ height: 34 }} />
            {/* Sur mobile, une section ouverte : logo seul */}
            <Typography
              sx={{ fontWeight: 700, fontSize: '1.15rem', display: { xs: title ? 'none' : 'block', sm: 'block' } }}
            >
              JustiSch
            </Typography>
          </Box>
          {title && (
            <>
              <Typography aria-hidden sx={{ mx: { xs: 1, sm: 1.5 }, color: 'text.disabled', fontSize: '1.4rem', fontWeight: 300 }}>
                /
              </Typography>
              <Typography
                {...(isSubPage ? { component: Link, to: activeSection!.basePath } : {})}
                noWrap
                sx={{
                  minWidth: 0,
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: isSubPage ? 'text.secondary' : 'text.primary',
                  textDecoration: 'none',
                  ...(isSubPage && { '&:hover': { color: 'primary.main' } }),
                }}
              >
                {title}
              </Typography>
            </>
          )}
        </Box>
        <UserMenu />
      </Toolbar>
    </AppBar>
  );
}

function App() {
  const status = useAuthStore((s) => s.status);
  const load = useAuthStore((s) => s.load);
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.includes(location.pathname);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {!isPublic && status === 'authenticated' && <TopBar />}
      <Suspense fallback={<LinearProgress />}>
        <Routes>
          <Route
            path="/login"
            element={status === 'authenticated' ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route path="/activation" element={<ActivationPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compte"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facturation"
            element={
              <ProtectedRoute adminOnly>
                <CostsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facturation/:month"
            element={
              <ProtectedRoute adminOnly>
                <CostDetailPage />
              </ProtectedRoute>
            }
          />
          {TOOLS.flatMap((tool) =>
            tool.routes.map(({ path, component: Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute tool={tool.id}>
                    <Component />
                  </ProtectedRoute>
                }
              />
            ))
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
